import { useCallback, useEffect, useRef, useState } from 'react'
import type { ServerApiClient } from '../../../../data/ServerApiClient'
import { trimStr } from '../cfoFormat'
import { accessSessionIdentity } from '../../../../data/accessSessionIdentity'
import type {
  CeoCockpitPeriodRun,
  CeoCockpitResponse,
  CeoCockpitSnapshotIndex,
} from './ceoCockpitTypes'

export type CeoCockpitLoadStatus = 'loading' | 'ready' | 'not_ready' | 'error'

export type UseCeoCockpitResult = {
  payload: CeoCockpitResponse | null
  status: CeoCockpitLoadStatus
  error: string | null
  periodRun: CeoCockpitPeriodRun | null
  reload: () => void
  reconstructPeriod: () => void
}

// Reads are cheap and freshly authorized. No company payload is reused across
// opens or sessions; this public hook remains for callers that invalidate after
// explicit actions, with no retained cache to clear.
export function invalidateCeoCockpitCache(_workbookId?: string | null) {}

export function useCeoCockpit(
  appApi: ServerApiClient,
  workbookId: string | null,
  snapshotDate?: string | null,
  retained = false,
): UseCeoCockpitResult {
  const date = trimStr(snapshotDate) || null
  const sessionIdentity = accessSessionIdentity(appApi.getToken?.())
  const requestedKey = cacheKeyFor(trimStr(workbookId), date, sessionIdentity, retained)
  const [payload, setPayload] = useState<CeoCockpitResponse | null>(null)
  const [payloadKey, setPayloadKey] = useState(requestedKey)
  const [status, setStatus] = useState<CeoCockpitLoadStatus>(() => (trimStr(workbookId) ? 'loading' : 'error'))
  const [error, setError] = useState<string | null>(null)
  const [periodRun, setPeriodRun] = useState<CeoCockpitPeriodRun | null>(null)
  const [periodRunKey, setPeriodRunKey] = useState(requestedKey)
  const [nonce, setNonce] = useState(0)
  const explicitRunKey = useRef<string | null>(null)

  const reload = useCallback(() => {
    invalidateCeoCockpitCache(workbookId)
    setNonce((value) => value + 1)
  }, [workbookId])

  const reconstructPeriod = useCallback(() => {
    if (!date || !trimStr(workbookId)) return
    explicitRunKey.current = requestedKey
    invalidateCeoCockpitCache(workbookId)
    setNonce((value) => value + 1)
  }, [workbookId, date, requestedKey])

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    const wb = trimStr(workbookId)
    if (!wb) {
      setPayload(null)
      setPayloadKey(cacheKeyFor(wb, date, sessionIdentity, retained))
      setStatus('error')
      setError('Missing workbook')
      return
    }

    const cacheKey = cacheKeyFor(wb, date, sessionIdentity, retained)
    // Never leave a payload from a different date on screen while an exact
    // historical request is loading.
    setPayload(null)
    setPayloadKey(cacheKey)
    setPeriodRun(null)
    setPeriodRunKey(cacheKey)

    const load = typeof appApi.getQuickBooksCeoCockpit === 'function'
      ? () => appApi.getQuickBooksCeoCockpit(wb, date ? { date, ...(retained ? { retained: true } : {}) } : undefined)
      : null
    if (!load) {
      setStatus('error')
      setError('CEO Cockpit API is not available')
      return
    }

    const sessionIsCurrent = () => !cancelled && accessSessionIdentity(appApi.getToken?.()) === sessionIdentity
    const request = () => {
      load()
        .then((next) => {
        if (!sessionIsCurrent()) return
        setPayload(next)
        setPayloadKey(cacheKey)
        setStatus('ready')
        setError(null)
        })
        .catch((err: any) => {
        if (!sessionIsCurrent()) return
        setPayload(null)
        setPayloadKey(cacheKey)
        setStatus('error')
        setError(err?.message || String(err) || 'CEO Cockpit source is unavailable')
        })
    }

    const startPeriodRun = typeof appApi.startQuickBooksCeoCockpitPeriodRun === 'function'
      ? () => appApi.startQuickBooksCeoCockpitPeriodRun(wb, date as string)
      : null
    const getPeriodRun = typeof appApi.getQuickBooksCeoCockpitPeriodRun === 'function'
      ? (runId: string) => appApi.getQuickBooksCeoCockpitPeriodRun(wb, runId)
      : null

    const acceptPeriodRun = (run: CeoCockpitPeriodRun) => {
      if (!sessionIsCurrent()) return
      setPeriodRun(run)
      setPeriodRunKey(cacheKey)
      if (run.status === 'ready') {
        setStatus('loading')
        request()
        return
      }
      if (run.status === 'failed') {
        const message = run.error || 'The selected period could not be prepared.'
        setPayload(null)
        setPayloadKey(cacheKey)
        setStatus('error')
        setError(message)
        return
      }
      setPayload(null)
      setPayloadKey(cacheKey)
      setStatus('not_ready')
      setError(null)
      if (!getPeriodRun) return
      retryTimer = setTimeout(() => {
        getPeriodRun(run.run_id)
          .then(acceptPeriodRun)
          .catch((err: any) => {
            if (!sessionIsCurrent()) return
            setStatus('error')
            setError(err?.message || String(err) || 'Period progress could not be loaded')
          })
      }, 1_000)
    }

    setStatus('loading')
    const explicitlyRequested = explicitRunKey.current === cacheKey
    if (explicitlyRequested) explicitRunKey.current = null
    if (explicitlyRequested && date && startPeriodRun && getPeriodRun) {
      startPeriodRun()
        .then(acceptPeriodRun)
        .catch((err: any) => {
          if (!sessionIsCurrent()) return
          setStatus('error')
          setError(err?.message || String(err) || 'The selected period could not be started')
        })
    } else {
      request()
    }

    return () => {
      cancelled = true
      if (retryTimer !== null) clearTimeout(retryTimer)
    }
  }, [appApi, workbookId, date, retained, sessionIdentity, nonce])

  const keyMatches = payloadKey === requestedKey
  return {
    payload: keyMatches ? payload : null,
    status: keyMatches ? status : 'loading',
    error: keyMatches ? error : null,
    periodRun: keyMatches && periodRunKey === requestedKey ? periodRun : null,
    reload,
    reconstructPeriod,
  }
}

function cacheKeyFor(workbookId: string, date: string | null, session: string | null, retained: boolean): string {
  return JSON.stringify([workbookId, date || 'latest', session, retained ? 'retained' : 'native'])
}

export type UseCeoCockpitSnapshotsResult = {
  index: CeoCockpitSnapshotIndex | null
  error: string | null
}

/** Discover completed dates for calendar suggestions and retained-audit access. */
export function useCeoCockpitSnapshots(
  appApi: ServerApiClient,
  workbookId: string | null,
): UseCeoCockpitSnapshotsResult {
  const sessionIdentity = accessSessionIdentity(appApi.getToken?.())
  const scopeKey = JSON.stringify([workbookId, sessionIdentity])
  const [indexKey, setIndexKey] = useState(scopeKey)
  const [index, setIndex] = useState<CeoCockpitSnapshotIndex | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const sessionIsCurrent = () => !cancelled && accessSessionIdentity(appApi.getToken?.()) === sessionIdentity
    setIndex(null)
    setIndexKey(scopeKey)
    const wb = trimStr(workbookId)
    const load = typeof appApi.getQuickBooksCeoCockpitSnapshots === 'function'
      ? () => appApi.getQuickBooksCeoCockpitSnapshots(wb)
      : null
    if (!wb || !load) {
      setIndex(null)
      setError(null)
      return
    }
    load()
      .then((next) => {
        if (!sessionIsCurrent()) return
        setIndex(next)
        setError(null)
      })
      .catch((err: any) => {
        if (!sessionIsCurrent()) return
        setIndex(null)
        setError(err?.message || String(err) || 'Snapshot history failed to load')
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId, sessionIdentity, scopeKey])

  return { index: indexKey === scopeKey ? index : null, error: indexKey === scopeKey ? error : null }
}
